import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Save, 
  Send, 
  GripVertical, 
  Eye, 
  Settings,
  Type,
  Play,
  Code,
  HelpCircle,
  FileText,
  Trash2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Upload,
  Image,
  X,
  BookOpen,
  AlertCircle,
  List,
  Plus,
  Menu,
  Sparkles,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { marked } from 'marked';
import TextStep from '../components/steps/TextStep';
import AudioPlayer from '../components/AudioPlayer';

// Configure marked for better rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});
import VideoStep from '../components/steps/VideoStep';
import CodeStep from '../components/steps/CodeStep';
import QuizStep from '../components/steps/QuizStep';
import PdfStep from '../components/steps/PdfStep';
import MediaUploader, { MediaFile } from '../components/MediaUploader';
import MediaLibrary from '../components/MediaLibrary';
import PDFImportModal from '../components/PDFImportModal';
import { TutorialStep, apiService, ContentType } from '../services/api';
import { useProducts } from '../hooks/useApi';
import { cn } from '../utils/cn';
import toast from 'react-hot-toast';
import CategoryManager from '../components/CategoryManager';
import GlossaryExtractionModal from '../components/GlossaryExtractionModal';

interface TutorialBuilderState {
  title: string;
  description: string;
  type: ContentType;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  category: string;
  product: string;
  language: 'en' | 'es';
  layout?: 'steps' | 'continuous'; // Layout mode: step-by-step navigation or continuous scroll
  steps: TutorialStep[];
  selectedStepId: string | null;
}

interface ValidationErrors {
  title?: string;
  description?: string;
  estimatedTime?: string;
  category?: string;
  product?: string;
  steps?: string;
}

const TutorialBuilder: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  
  // Fetch products for product selection
  const { data: productsData } = useProducts();
  const products = productsData?.products || [];
  
  const [state, setState] = useState<TutorialBuilderState>({
    title: '',
    description: '',
    type: 'guide',
    difficulty: 'beginner',
    estimatedTime: '',
    category: 'Getting Started',
    product: '',
    language: 'en',
    layout: 'steps', // Default to step-by-step layout
    steps: [],
    selectedStepId: null
  });

  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [showMediaUpload, setShowMediaUpload] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showPDFImport, setShowPDFImport] = useState(false);
  const [showGlossaryExtraction, setShowGlossaryExtraction] = useState(false);
  const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState(false);
  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);
  // All sections are always expanded - no need for state
  
  // Check URL params for PDF import method
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('method') === 'pdf') {
      setShowPDFImport(true);
    }
  }, []);
  const [isSaving, setIsSaving] = useState(false);
  const [isPdfImported, setIsPdfImported] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{
    isUploading: boolean;
    progress: number;
    message: string;
  }>({
    isUploading: false,
    progress: 0,
    message: ''
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryObjects, setCategoryObjects] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  const stepTypes = [
    { type: 'text', icon: Type, label: 'Text', color: 'from-blue-500 to-blue-600' },
    { type: 'video', icon: Play, label: 'Video', color: 'from-red-500 to-red-600' },
    { type: 'code', icon: Code, label: 'Code', color: 'from-green-500 to-green-600' },
    { type: 'quiz', icon: HelpCircle, label: 'Quiz', color: 'from-purple-500 to-purple-600' },
    { type: 'pdf', icon: FileText, label: 'PDF', color: 'from-orange-500 to-orange-600' },
    { type: 'work-instruction-step', icon: BookOpen, label: 'Work Instruction', color: 'from-indigo-500 to-indigo-600' },
    { type: 'safety-warning', icon: AlertCircle, label: 'Safety Warning', color: 'from-yellow-500 to-yellow-600' },
    { type: 'material-list', icon: List, label: 'Material List', color: 'from-teal-500 to-teal-600' }
  ];

  // Load tutorial data when editing
  useEffect(() => {
    if (isEditing && id) {
      loadTutorialData(id);
    }
  }, [isEditing, id]);

  // Load categories
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await apiService.getCategories();
      
      if (response.success && response.data) {
        const catData = response.data as any[];
        setCategoryObjects(catData);
        setCategories(catData.map((cat: any) => cat.name || cat));
      } else {
        // Fallback to hardcoded categories if API fails
        setCategories(['Getting Started', 'Product Guides', 'API Docs', 'Workflows', 'Manufacturing']);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback to hardcoded categories
      setCategories(['Getting Started', 'Product Guides', 'API Docs', 'Workflows', 'Manufacturing']);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadTutorialData = async (tutorialId: string) => {
    try {
      setIsLoading(true);
      const tutorial = await apiService.getTutorialById(tutorialId);
      
      // Process steps and ensure all fields are properly initialized
      // Remove tutorialId and other backend-only fields
      const processedSteps = (tutorial.steps || []).map((step, index) => {
        const { tutorialId, ...cleanStep } = step as any;
        return {
          ...cleanStep,
          id: cleanStep.id || `step-${index}-${Date.now()}`,
          videoUrl: cleanStep.videoUrl || '',
          pdfUrl: cleanStep.pdfUrl || '',
          pdfFile: null // Files can't be restored from API, only URLs
        };
      });
      
      setState({
        title: tutorial.title,
        description: tutorial.description || '',
        type: tutorial.type || 'guide',
        difficulty: tutorial.difficulty,
        estimatedTime: tutorial.estimatedTime,
        category: tutorial.category,
        product: tutorial.product || '',
        language: tutorial.language || 'en',
        layout: tutorial.layout || 'steps',
        steps: processedSteps,
        selectedStepId: processedSteps[0]?.id || null
      });
    } catch (error) {
      console.error('Error loading tutorial:', error);
      toast.error('Failed to load tutorial data');
      navigate('/tutorials');
    } finally {
      setIsLoading(false);
    }
  };

  // Categories are now loaded dynamically
  const contentTypes: { value: ContentType; label: string }[] = [
    { value: 'guide', label: 'Guide' },
    { value: 'course', label: 'Course' },
    { value: 'presentation', label: 'Presentation' }
  ];
  const difficulties = [
    { value: 'beginner', label: 'Beginner', color: 'bg-green-100 text-green-800' },
    { value: 'intermediate', label: 'Intermediate', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'advanced', label: 'Advanced', color: 'bg-red-100 text-red-800' }
  ];

  const addStep = (type: string) => {
    const newStep: TutorialStep = {
      id: Date.now().toString(),
      title: `New ${type} step`,
      content: '',
      type: type as any,
      order: state.steps.length,
      videoUrl: '',
      pdfUrl: '',
      pdfFile: null
    };
    
    const updatedSteps = [...state.steps, newStep];
    setState(prev => ({
      ...prev,
      steps: updatedSteps,
      selectedStepId: newStep.id
    }));
  };

  const updateStep = (stepId: string, updates: Partial<TutorialStep>) => {
    const updatedSteps = state.steps.map(step =>
      step.id === stepId ? { ...step, ...updates } : step
    );
    setState(prev => ({ ...prev, steps: updatedSteps }));
  };

  const deleteStep = (stepId: string) => {
    const updatedSteps = state.steps.filter(step => step.id !== stepId);
    const newSelectedId = updatedSteps.length > 0 ? updatedSteps[0].id : null;
    setState(prev => ({
      ...prev,
      steps: updatedSteps,
      selectedStepId: newSelectedId
    }));
  };

  const moveStep = (stepId: string, direction: 'up' | 'down') => {
    const currentIndex = state.steps.findIndex(step => step.id === stepId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= state.steps.length) return;

    const updatedSteps = [...state.steps];
    [updatedSteps[currentIndex], updatedSteps[newIndex]] = [updatedSteps[newIndex], updatedSteps[currentIndex]];
    
    // Update order property
    updatedSteps.forEach((step, index) => {
      step.order = index;
    });

    setState(prev => ({ ...prev, steps: updatedSteps }));
  };

  const handlePDFImport = (tutorial: {
    title: string;
    description: string;
    type: 'guide' | 'course' | 'presentation';
    difficulty: 'beginner' | 'intermediate' | 'advanced';
    estimatedTime: string;
    steps: TutorialStep[];
  }) => {
    setState(prev => ({
      ...prev,
      title: tutorial.title,
      description: tutorial.description,
      type: tutorial.type,
      difficulty: tutorial.difficulty,
      estimatedTime: tutorial.estimatedTime,
      steps: tutorial.steps.map((step, index) => ({
        ...step,
        id: step.id || `step-${index}-${Date.now()}`,
        order: index,
        videoUrl: step.videoUrl || '',
        pdfUrl: step.pdfUrl || '',
        pdfFile: null
      })),
      selectedStepId: tutorial.steps[0]?.id || null
    }));
    
    setIsPdfImported(true); // Mark as PDF imported
    toast.success('Tutorial imported from PDF successfully!');
    setShowPDFImport(false);
  };

  const selectedStep = state.steps.find(step => step.id === state.selectedStepId);

  // Recursively remove tutorialId from any object
  const removeTutorialId = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
      return obj.map(item => removeTutorialId(item));
    }
    if (typeof obj === 'object') {
      const cleaned: any = {};
      for (const key in obj) {
        if (key === 'tutorialId' || key === 'pdfFile') {
          continue; // Skip these fields
        }
        cleaned[key] = removeTutorialId(obj[key]);
      }
      return cleaned;
    }
    return obj;
  };

  // Ensure quiz questions have required fields before sending to API
  const normalizeStepForSave = (step: TutorialStep, index: number) => {
    // Create a clean copy without any backend-only fields
    const stepAny = step as any;
    const cleanStep: any = {
      id: stepAny.id,
      title: stepAny.title,
      content: stepAny.content || '',
      type: stepAny.type,
      order: index + 1,
      videoUrl: stepAny.videoUrl || '',
      pdfUrl: stepAny.pdfUrl || ''
    };

    // Include metadata if it exists (but remove tutorialId from nested objects)
    if (stepAny.metadata) {
      cleanStep.metadata = removeTutorialId(stepAny.metadata);
    }
    
    // Include subsections if they exist (but remove tutorialId from nested objects)
    if (stepAny.subsections && Array.isArray(stepAny.subsections)) {
      cleanStep.subsections = stepAny.subsections.map((sub: any) => removeTutorialId(sub));
    }

    // Handle quiz questions
    if (stepAny.type === 'quiz' && Array.isArray(stepAny.questions)) {
      cleanStep.questions = stepAny.questions.map((q: any, qi: number) => {
        const cleanedQ = removeTutorialId(q);
        return {
          ...cleanedQ,
          title: (cleanedQ.title && cleanedQ.title.trim())
            ? cleanedQ.title
            : (cleanedQ.question?.trim()?.slice(0, 80) || `Question ${qi + 1}`)
        };
      });
    } else if (stepAny.questions) {
      cleanStep.questions = removeTutorialId(stepAny.questions);
    }

    // Final cleanup - recursively remove tutorialId from the entire step
    const finalClean = removeTutorialId(cleanStep);
    
    // Ensure these specific fields are deleted
    delete finalClean.tutorialId;
    delete finalClean.pdfFile;

    return finalClean;
  };

  const handleSaveDraft = async () => {
    if (isSaving) return;
    
    // Basic validation
    if (!state.title.trim()) {
      toast.error('Please enter a tutorial title');
      return;
    }
    if (state.steps.length === 0) {
      toast.error('Please add at least one step to your tutorial');
      return;
    }
    
    
    try {
      setIsSaving(true);
      
      // Clean steps before creating tutorial data
      const cleanedSteps = state.steps.map((step, index) => {
        const normalized = normalizeStepForSave(step, index);
        // Final safety check: recursively remove tutorialId
        return removeTutorialId(normalized);
      });
      
      if (isEditing && id) {
        // For updates, exclude 'type' field as it's not allowed in update schema
        const tutorialData = {
          title: state.title,
          description: state.description || '',
          difficulty: state.difficulty,
          estimatedTime: state.estimatedTime,
          category: state.category,
          product: state.product,
          language: state.language,
          layout: state.layout || 'steps',
          steps: cleanedSteps,
          isPublished: false
        };
        
        // Debug: Log the first step to check for tutorialId
        if (tutorialData.steps.length > 0) {
          console.log('First step before save:', JSON.stringify(tutorialData.steps[0], null, 2));
          // Verify no tutorialId exists
          const hasTutorialId = JSON.stringify(tutorialData.steps[0]).includes('tutorialId');
          if (hasTutorialId) {
            console.error('ERROR: tutorialId still present in step!', tutorialData.steps[0]);
          }
        }
        
        await apiService.updateTutorial(id, tutorialData);
        toast.success('Tutorial updated successfully!');
        // Navigate to admin tutorials management after updating
        navigate('/admin/tutorials');
      } else {
        // For creates, include 'type' field as it's required in create schema
        const tutorialData = {
          title: state.title,
          description: state.description || '',
          type: state.type,
          difficulty: state.difficulty,
          estimatedTime: state.estimatedTime,
          category: state.category,
          product: state.product,
          language: state.language,
          layout: state.layout || 'steps',
          steps: cleanedSteps,
          isPublished: false
        };
        
        // Use PDF-generated endpoint if tutorial was imported from PDF
        const created = isPdfImported 
          ? await apiService.createPdfGeneratedTutorial(tutorialData)
          : await apiService.createTutorial(tutorialData);
        
        toast.success('Tutorial saved as draft successfully!');
        
        // Start Spanish translation after a short delay
        setTimeout(async () => {
          try {
            const translationResponse = await apiService.request('/translation/start', {
              method: 'POST',
              body: JSON.stringify({
                tutorialId: created?.id,
                targetLanguage: 'es'
              })
            });

            if (translationResponse.success) {
            }
          } catch (error) {
            console.error('Failed to start Spanish translation:', error);
          }
        }, 2000); // 2 second delay
        
        // Navigate to admin tutorials management after creating
        navigate('/admin/tutorials');
      }
    } catch (error: any) {
      console.error('Error saving tutorial:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Handle validation errors
      if (error.response?.data?.details) {
        const errors: ValidationErrors = {};
        error.response.data.details.forEach((detail: string) => {
          // Parse specific validation messages
          if (detail.includes('"title"')) {
            errors.title = detail.replace(/"/g, '');
          } else if (detail.includes('"description"')) {
            errors.description = detail.replace(/"/g, '');
          } else if (detail.includes('"estimatedTime"')) {
            errors.estimatedTime = detail.replace(/"/g, '');
          } else if (detail.includes('"category"')) {
            errors.category = detail.replace(/"/g, '');
          } else if (detail.includes('"product"')) {
            errors.product = detail.replace(/"/g, '');
          } else if (detail.includes('"steps"')) {
            errors.steps = detail.replace(/"/g, '');
          }
        });
        setValidationErrors(errors);
        toast.error('Please fix the validation errors below');
      } else {
        toast.error('Failed to save tutorial. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handlePublish = async () => {
    if (isPublishing) return;
    
    // Basic validation
    if (!state.title.trim()) {
      toast.error('Please enter a tutorial title');
      return;
    }
    if (state.steps.length === 0) {
      toast.error('Please add at least one step to your tutorial');
      return;
    }
    
    
    try {
      setIsPublishing(true);
      
      // Start upload progress tracking
      setUploadProgress({
        isUploading: true,
        progress: 0,
        message: 'Preparing tutorial data...'
      });

      // Clean steps before creating tutorial data
      const cleanedSteps = state.steps.map((step, index) => {
        const normalized = normalizeStepForSave(step, index);
        // Final safety check: recursively remove tutorialId
        return removeTutorialId(normalized);
      });
      
      // Update progress
      setUploadProgress({
        isUploading: true,
        progress: 30,
        message: 'Sending tutorial to server...'
      });

      if (isEditing && id) {
        // For updates, exclude 'type' field as it's not allowed in update schema
        const tutorialData = {
          title: state.title,
          description: state.description || '',
          difficulty: state.difficulty,
          estimatedTime: state.estimatedTime,
          category: state.category,
          product: state.product,
          language: state.language,
          layout: state.layout || 'steps',
          steps: cleanedSteps,
          isPublished: true
        };
        
        await apiService.updateTutorial(id, tutorialData);
        toast.success('Tutorial published successfully!');
        // Navigate to admin tutorials management after publishing
        navigate('/admin/tutorials');
      } else {
        // For creates, include 'type' field as it's required in create schema
        const tutorialData = {
          title: state.title,
          description: state.description || '',
          type: state.type,
          difficulty: state.difficulty,
          estimatedTime: state.estimatedTime,
          category: state.category,
          product: state.product,
          language: state.language,
          layout: state.layout || 'steps',
          steps: cleanedSteps,
          isPublished: true
        };
        
        setUploadProgress({
          isUploading: true,
          progress: 60,
          message: 'Creating tutorial...'
        });
        
        // Use PDF-generated endpoint if tutorial was imported from PDF
        await (isPdfImported 
          ? apiService.createPdfGeneratedTutorial(tutorialData)
          : apiService.createTutorial(tutorialData));
        
        setUploadProgress({
          isUploading: true,
          progress: 100,
          message: 'Tutorial created successfully!'
        });
        
        toast.success('Tutorial published successfully!');
        // Navigate to admin tutorials management after publishing
        navigate('/admin/tutorials');
      }
    } catch (error: any) {
      console.error('Error publishing tutorial:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Handle validation errors
      if (error.response?.data?.details) {
        const errors: ValidationErrors = {};
        error.response.data.details.forEach((detail: string) => {
          // Parse specific validation messages
          if (detail.includes('"title"')) {
            errors.title = detail.replace(/"/g, '');
          } else if (detail.includes('"description"')) {
            errors.description = detail.replace(/"/g, '');
          } else if (detail.includes('"estimatedTime"')) {
            errors.estimatedTime = detail.replace(/"/g, '');
          } else if (detail.includes('"category"')) {
            errors.category = detail.replace(/"/g, '');
          } else if (detail.includes('"product"')) {
            errors.product = detail.replace(/"/g, '');
          } else if (detail.includes('"steps"')) {
            errors.steps = detail.replace(/"/g, '');
          }
        });
        setValidationErrors(errors);
        toast.error('Please fix the validation errors below');
      } else {
        toast.error('Failed to publish tutorial. Please try again.');
      }
    } finally {
      setIsPublishing(false);
      setUploadProgress({
        isUploading: false,
        progress: 0,
        message: ''
      });
    }
  };

  const handleMediaUpload = (files: MediaFile[]) => {
    // Save uploaded files to localStorage
    try {
      const existingFiles = JSON.parse(localStorage.getItem('media-library') || '[]');
      const updatedFiles = [...existingFiles, ...files];
      localStorage.setItem('media-library', JSON.stringify(updatedFiles));
      setShowMediaUpload(false);
    } catch (error) {
      console.error('Error saving media files:', error);
    }
  };

  // Clear validation errors when fields are updated
  const updateField = (field: keyof TutorialBuilderState, value: any) => {
    setState(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const renderStepEditor = () => {
    if (!selectedStep) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <div className="text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No step selected</p>
            <p className="text-sm">Select a step from the sidebar or add a new one</p>
          </div>
        </div>
      );
    }

    // Show preview mode
    if (isPreviewMode) {
      const layout = state.layout || 'steps';
      const currentStepIndex = selectedStep ? state.steps.findIndex(s => s.id === selectedStep.id) : 0;
      const currentStep = selectedStep || state.steps[0];
      
      // Continuous scroll layout - show all steps
      if (layout === 'continuous') {
        return (
          <div className="max-w-6xl mx-auto space-y-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{state.title}</h1>
              {state.description && (
                <p className="text-lg text-gray-600 mb-6">{state.description}</p>
              )}
            </div>
            
            {state.steps.map((step, index) => (
              <div key={step.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold text-sm">
                    {index + 1}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{step.title}</h2>
                </div>
                <div 
                  className="prose prose-lg max-w-none"
                  dangerouslySetInnerHTML={{ __html: marked(step.content || '') }}
                />
                {step.type === 'video' && step.videoUrl && (
                  <div className="mt-4">
                    <video controls className="w-full rounded-lg">
                      <source src={step.videoUrl} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}
                {step.type === 'pdf' && step.pdfUrl && (
                  <div className="mt-4">
                    <iframe
                      src={step.pdfUrl}
                      className="w-full h-96 border border-gray-300 rounded-lg"
                      title={step.title}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }
      
      // Step-by-step layout - show one step at a time with navigation
      return (
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-4">{state.title}</h1>
              <p className="text-lg text-gray-600 mb-4">{state.description}</p>
              <div className="flex items-center space-x-4 text-sm text-gray-500">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                  {state.difficulty}
                </span>
                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full">
                  {state.estimatedTime}
                </span>
                <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                  {state.category}
                </span>
                {state.product && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
                    {state.product}
                  </span>
                )}
              </div>
            </div>
            
            {currentStep && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-500">
                    Step {currentStepIndex + 1} of {state.steps.length}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        if (currentStepIndex > 0) {
                          setState(prev => ({ ...prev, selectedStepId: state.steps[currentStepIndex - 1].id }));
                        }
                      }}
                      disabled={currentStepIndex === 0}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => {
                        if (currentStepIndex < state.steps.length - 1) {
                          setState(prev => ({ ...prev, selectedStepId: state.steps[currentStepIndex + 1].id }));
                        }
                      }}
                      disabled={currentStepIndex === state.steps.length - 1}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
                
                <div className="border-l-4 border-blue-500 pl-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">
                    {currentStep.title}
                  </h2>
                  <div 
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: marked(currentStep.content || '') }}
                  />
                  {currentStep.type === 'video' && currentStep.videoUrl && (
                    <div className="mt-4">
                      <video controls className="w-full rounded-lg">
                        <source src={currentStep.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                      </video>
                    </div>
                  )}
                  {currentStep.type === 'pdf' && currentStep.pdfUrl && (
                    <div className="mt-4">
                      <iframe
                        src={currentStep.pdfUrl}
                        className="w-full h-96 border border-gray-300 rounded-lg"
                        title={currentStep.title}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        );
    }

    // Regular editor mode - render step editor  
    const commonProps = {
      onSave: () => {}
    };

    switch (selectedStep.type) {
      case 'text':
        return (
          <TextStep
            content={selectedStep.content}
            onChange={(content) => updateStep(selectedStep.id, { content })}
            {...commonProps}
          />
        );
      case 'video':
        return (
          <VideoStep
            videoUrl={selectedStep.videoUrl || ''}
            title={selectedStep.title}
            onChange={(data) => updateStep(selectedStep.id, { 
              videoUrl: data.videoUrl,
              title: data.title
            })}
            {...commonProps}
          />
        );
      case 'code':
        return (
          <CodeStep
            code={selectedStep.content}
            title={selectedStep.title}
            onChange={(data) => updateStep(selectedStep.id, { 
              content: data.code,
              title: data.title
            })}
            {...commonProps}
          />
        );
      case 'quiz':
        return (
          <QuizStep
            title={selectedStep.title}
            questions={selectedStep.questions || []}
            onChange={(data) => updateStep(selectedStep.id, { 
              title: data.title,
              questions: data.questions
            })}
            {...commonProps}
          />
        );
      case 'pdf':
        return (
          <PdfStep
            title={selectedStep.title}
            pdfUrl={selectedStep.pdfUrl || ''}
            pdfFile={selectedStep.pdfFile || null}
            content={selectedStep.content}
            onChange={(content) => updateStep(selectedStep.id, { content })}
            onPdfChange={(url, file) => updateStep(selectedStep.id, { pdfUrl: url, pdfFile: file })}
            {...commonProps}
          />
        );
      case 'work-instruction-step':
      case 'safety-warning':
      case 'material-list':
        // These step types use TextStep with subtle left border for distinction
        const borderColor = selectedStep.type === 'work-instruction-step' ? 'border-l-indigo-500' :
                           selectedStep.type === 'safety-warning' ? 'border-l-yellow-500' :
                           'border-l-teal-500';
        
        return (
          <div className={cn("border-l-4 pl-4", borderColor)}>
            <TextStep
              content={selectedStep.content}
              onChange={(content) => updateStep(selectedStep.id, { content })}
              {...commonProps}
            />
            {/* Show metadata if available */}
            {selectedStep.metadata && (
              <div className="mt-4 pt-4 border-t border-gray-200 text-xs text-gray-500">
                {selectedStep.metadata.materials && selectedStep.metadata.materials.length > 0 && (
                  <div className="mb-2">
                    <span className="font-medium">Materials:</span> {selectedStep.metadata.materials.join(', ')}
                  </div>
                )}
                {selectedStep.metadata.tools && selectedStep.metadata.tools.length > 0 && (
                  <div className="mb-2">
                    <span className="font-medium">Tools:</span> {selectedStep.metadata.tools.join(', ')}
                  </div>
                )}
                {selectedStep.metadata.warnings && selectedStep.metadata.warnings.length > 0 && (
                  <div>
                    <span className="font-medium">Warnings:</span> {selectedStep.metadata.warnings.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      default:
        // For any other step types, treat them as text steps
        return (
          <TextStep
            content={selectedStep.content}
            onChange={(content) => updateStep(selectedStep.id, { content })}
            {...commonProps}
          />
        );
    }
  };

  // Show loading state while fetching tutorial data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-xl mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Loading Tutorial...</h2>
          <p className="text-gray-600">Please wait while we load the tutorial data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <div className="flex-1">
                <input
                  type="text"
                  value={state.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder={isEditing ? "Edit content title..." : "Content title..."}
                  className={cn(
                    "text-xl font-semibold bg-transparent border-none outline-none placeholder-gray-400 w-full",
                    validationErrors.title && "border-b-2 border-red-500"
                  )}
                />
                {validationErrors.title && (
                  <p className="text-sm text-red-500 mt-1">{validationErrors.title}</p>
                )}
              </div>
              {isEditing && (
                <p className="text-sm text-gray-500 mt-1">Editing existing tutorial</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-3 relative">
            <button
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              className={cn(
                "flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors",
                isPreviewMode 
                  ? "bg-blue-100 text-blue-700" 
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              )}
            >
              <Eye className="w-4 h-4" />
              <span>Preview</span>
            </button>
            <button
              onClick={() => setShowGlossaryExtraction(true)}
              disabled={!state.title || state.steps.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Sparkles className="w-4 h-4" />
              <span>Extract Technical Terms</span>
            </button>
            <button
              onClick={handleSaveDraft}
              disabled={isSaving}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{isSaving ? 'Saving...' : (isEditing ? 'Update Draft' : 'Save Draft')}</span>
            </button>
            <button
              onClick={handlePublish}
              disabled={isPublishing}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              <span>{isPublishing ? 'Publishing...' : (isEditing ? 'Update & Publish' : 'Publish')}</span>
            </button>
            
            {/* Progress indicator */}
            {uploadProgress.isUploading && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-50">
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>{uploadProgress.message}</span>
                      <span>{uploadProgress.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-[#667eea] to-[#764ba2] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress.progress}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex h-[calc(100vh-80px)]">
        {/* Left Sidebar - Collapsible */}
        <div className={cn(
          "bg-white border-r border-gray-200 flex flex-col transition-all duration-300 h-full",
          isLeftSidebarCollapsed ? "w-20 overflow-hidden" : "w-80 overflow-y-auto"
        )}>
          {!isLeftSidebarCollapsed ? (
            <div className="flex flex-col">
              {/* Tutorial Metadata - Always Expanded */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <h3 className="font-semibold text-gray-900 mb-4">Content Details</h3>
                
                <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content Type
                </label>
                <select
                  value={state.type}
                  onChange={(e) => setState(prev => ({ ...prev, type: e.target.value as ContentType }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm"
                >
                  {contentTypes.map(ct => (
                    <option key={ct.value} value={ct.value}>{ct.label}</option>
                  ))}
                </select>
              </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={state.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  placeholder="Describe what this tutorial covers..."
                  rows={3}
                  className={cn(
                    "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm",
                    validationErrors.description ? "border-red-500" : "border-gray-300"
                  )}
                />
                {validationErrors.description && (
                  <p className="text-sm text-red-500 mt-1">{validationErrors.description}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Difficulty
                  </label>
                  <select
                    value={state.difficulty}
                    onChange={(e) => setState(prev => ({ ...prev, difficulty: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm"
                  >
                    {difficulties.map(diff => (
                      <option key={diff.value} value={diff.value}>
                        {diff.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time
                  </label>
                  <input
                    type="text"
                    value={state.estimatedTime}
                    onChange={(e) => updateField('estimatedTime', e.target.value)}
                    placeholder="15 min"
                    className={cn(
                      "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm",
                      validationErrors.estimatedTime ? "border-red-500" : "border-gray-300"
                    )}
                  />
                  {validationErrors.estimatedTime && (
                    <p className="text-sm text-red-500 mt-1">{validationErrors.estimatedTime}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Language
                </label>
                <select
                  value={state.language}
                  onChange={(e) => setState(prev => ({ ...prev, language: e.target.value as 'en' | 'es' }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm"
                >
                  <option value="en">🇺🇸 English</option>
                  <option value="es">🇪🇸 Spanish</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <div className="flex space-x-2">
                  <select
                    value={state.category}
                    onChange={(e) => setState(prev => ({ ...prev, category: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm"
                    disabled={loadingCategories}
                  >
                    {loadingCategories ? (
                      <option>Loading categories...</option>
                    ) : (
                      (() => {
                        // Build hierarchy display for categories
                        const buildCategoryOptions = (cats: any[], parentId: string | null = null, level: number = 0): Array<{name: string, value: string, display: string}> => {
                          return cats
                            .filter(cat => {
                              const catParentId = cat.parentCategoryId || null;
                              return catParentId === parentId;
                            })
                            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
                            .flatMap(cat => {
                              const displayName = level > 0 ? `${'  '.repeat(level)}└─ ${cat.name}` : cat.name;
                              const result = [{ name: cat.name, value: cat.name, display: displayName }];
                              const children = buildCategoryOptions(cats, cat.id, level + 1);
                              return [...result, ...children];
                            });
                        };
                        
                        const options = categoryObjects.length > 0 
                          ? buildCategoryOptions(categoryObjects)
                          : categories.map(name => ({ name, value: name, display: name }));
                        
                        return options.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.display}
                          </option>
                        ));
                      })()
                    )}
                  </select>
                  <button
                    onClick={() => setShowCategoryManager(true)}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    title="Manage Categories"
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Product (Optional)
                </label>
                <select
                  value={state.product}
                  onChange={(e) => updateField('product', e.target.value)}
                  className={cn(
                    "w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm",
                    validationErrors.product ? "border-red-500" : "border-gray-300"
                  )}
                >
                  <option value="">General Tutorial (No specific product)</option>
                  {products.map(product => (
                    <option key={product.id} value={product.name}>
                      {product.name}
                    </option>
                  ))}
                </select>
                {validationErrors.product && (
                  <p className="text-sm text-red-500 mt-1">{validationErrors.product}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty for general manufacturing tutorials
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Layout
                </label>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newLayout = 'steps' as 'steps' | 'continuous';
                      setState(prev => ({ ...prev, layout: newLayout }));
                      toast.success('Layout set to Steps mode. Click Preview to see the change.');
                    }}
                    className={cn(
                      "flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                      (state.layout || 'steps') === 'steps'
                        ? "bg-[#667eea] text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    )}
                    title="Step-by-step navigation"
                  >
                    Steps
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const newLayout = 'continuous' as 'steps' | 'continuous';
                      setState(prev => ({ ...prev, layout: newLayout }));
                      toast.success('Layout set to Continuous Scroll mode. Click Preview to see the change.');
                    }}
                    className={cn(
                      "flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                      state.layout === 'continuous'
                        ? "bg-[#667eea] text-white shadow-md"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    )}
                    title="Continuous scroll"
                  >
                    Scroll
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {state.layout === 'continuous' 
                    ? 'Users will see all steps in a continuous scroll' 
                    : 'Users will navigate step-by-step with Previous/Next buttons'}
                </p>
              </div>
            </div>
              </div>
              </div>

              {/* Media Management - Always Expanded */}
              <div className="p-4 border-b border-gray-200 flex-shrink-0">
                <h3 className="font-semibold text-gray-900 mb-4">Media</h3>
                
                <div className="space-y-2">
              <button
                onClick={() => setShowPDFImport(true)}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 transition-colors"
              >
                <FileText className="w-4 h-4" />
                <span>Import from PDF</span>
              </button>
              <button
                onClick={() => setShowMediaUpload(true)}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
              >
                <Upload className="w-4 h-4" />
                <span>Upload Media</span>
              </button>
              <button
                onClick={() => setShowMediaLibrary(true)}
                className="w-full flex items-center space-x-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <Image className="w-4 h-4" />
                <span>Media Library</span>
              </button>
                </div>
              </div>

              {/* Steps List - Always Expanded */}
              <div className="p-4 flex-shrink-0">
                <h3 className="font-semibold text-gray-900 mb-4">Steps ({state.steps.length})</h3>
                
                <>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {stepTypes.map(stepType => (
                        <button
                          key={stepType.type}
                          onClick={() => addStep(stepType.type)}
                          className={cn(
                            "flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
                            `bg-gradient-to-r ${stepType.color} text-white hover:opacity-80`
                          )}
                          title={`Add ${stepType.label} Step`}
                        >
                          <stepType.icon className="w-3.5 h-3.5" />
                          <span>{stepType.label}</span>
                        </button>
                      ))}
                    </div>

                    <div className="space-y-2">
                      {state.steps.map((step, index) => {
                const stepType = stepTypes.find(st => st.type === step.type);
                return (
                  <motion.div
                    key={step.id}
                    className={cn(
                      "p-3 rounded-lg border cursor-pointer transition-all",
                      state.selectedStepId === step.id
                        ? "border-[#667eea] bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    )}
                    onClick={() => setState(prev => ({ ...prev, selectedStepId: step.id }))}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex items-center space-x-3">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-medium",
                        `bg-gradient-to-r ${stepType?.color || 'from-gray-500 to-gray-600'}`
                      )}>
                        {stepType?.icon && <stepType.icon className="w-4 h-4" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {step.title}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                          {step.type === 'work-instruction-step' ? 'Work-Instruction-Step' :
                           step.type === 'safety-warning' ? 'Safety Warning' :
                           step.type === 'material-list' ? 'Material List' :
                           step.type} • Step {index + 1}
                        </p>
                      </div>
                      <div className="flex flex-col space-y-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveStep(step.id, 'up');
                          }}
                          disabled={index === 0}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            moveStep(step.id, 'down');
                          }}
                          disabled={index === state.steps.length - 1}
                          className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteStep(step.id);
                        }}
                        className="p-1 text-red-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                        </div>
                      </motion.div>
                    );
                  })}

                      {state.steps.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                          <p className="text-sm">No steps added yet</p>
                          <p className="text-xs">Click the buttons above to add steps</p>
                        </div>
                      )}
                    </div>
                  </>
              </div>
            </div>
          ) : (
            <div className="w-full flex flex-col items-center py-4 px-2 space-y-3">
              {/* Hamburger menu / Expand button */}
              <button
                onClick={() => setIsLeftSidebarCollapsed(false)}
                className="w-full p-3 bg-gradient-to-r from-[#667eea] to-[#764ba2] hover:from-[#5a6fd8] hover:to-[#6a4190] rounded-xl shadow-md transition-all flex items-center justify-center"
                title="Expand sidebar"
              >
                <Menu className="w-5 h-5 text-white" />
              </button>
              
              {/* Divider */}
              <div className="w-full h-px bg-gray-200"></div>
              
              {/* Quick action icons */}
              <div className="w-full flex flex-col items-center space-y-2">
                <button
                  onClick={() => {
                    setIsLeftSidebarCollapsed(false);
                  }}
                  className="w-full p-3 hover:bg-gray-100 rounded-lg flex items-center justify-center transition-colors"
                  title="Content Details"
                >
                  <Settings className="w-5 h-5 text-gray-700" />
                </button>
                
                <button
                  onClick={() => {
                    setIsLeftSidebarCollapsed(false);
                    setShowMediaUpload(true);
                  }}
                  className="w-full p-3 hover:bg-gray-100 rounded-lg flex items-center justify-center transition-colors"
                  title="Upload Media"
                >
                  <Upload className="w-5 h-5 text-gray-700" />
                </button>
                
                <button
                  onClick={() => {
                    setIsLeftSidebarCollapsed(false);
                  }}
                  className="w-full p-3 hover:bg-gray-100 rounded-lg flex items-center justify-center transition-colors"
                  title="Steps"
                >
                  <FileText className="w-5 h-5 text-gray-700" />
                </button>
                
                <div className="w-full h-px bg-gray-200 my-1"></div>
                
                <button
                  onClick={() => {
                    setIsLeftSidebarCollapsed(false);
                    addStep('text');
                  }}
                  className="w-full p-3 hover:bg-blue-100 rounded-lg flex items-center justify-center bg-blue-50 transition-colors"
                  title="Add Step"
                >
                  <Plus className="w-5 h-5 text-blue-600" />
                </button>
              </div>
            </div>
          )}
          
          {/* Collapse/Expand Toggle */}
          {!isLeftSidebarCollapsed && (
            <div className="p-2 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setIsLeftSidebarCollapsed(true)}
                className="w-full p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center"
                title="Collapse sidebar"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col bg-gray-50">
          {selectedStep ? (
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4 flex-1">
                  <input
                    type="text"
                    value={selectedStep.title}
                    onChange={(e) => updateStep(selectedStep.id, { title: e.target.value })}
                    className="text-lg font-semibold bg-transparent border-none outline-none focus:ring-2 focus:ring-[#667eea] focus:ring-offset-2 rounded px-2 py-1 -ml-2"
                    placeholder="Step title..."
                  />
                  <span className="text-sm text-gray-500">
                    {state.steps.findIndex(s => s.id === selectedStep.id) + 1} of {state.steps.length}
                  </span>
                  <span className={cn(
                    "px-2 py-1 rounded-full text-xs font-medium",
                    selectedStep.type === 'work-instruction-step' ? "bg-indigo-100 text-indigo-700" :
                    selectedStep.type === 'safety-warning' ? "bg-yellow-100 text-yellow-700" :
                    selectedStep.type === 'material-list' ? "bg-teal-100 text-teal-700" :
                    "bg-gray-100 text-gray-700"
                  )}>
                    {selectedStep.type === 'work-instruction-step' ? 'Work Instruction' :
                     selectedStep.type === 'safety-warning' ? 'Safety Warning' :
                     selectedStep.type === 'material-list' ? 'Material List' :
                     selectedStep.type.charAt(0).toUpperCase() + selectedStep.type.slice(1)}
                  </span>
                </div>
                <button
                  onClick={() => setIsPreviewMode(!isPreviewMode)}
                  className={cn(
                    "flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-colors",
                    isPreviewMode 
                      ? "bg-blue-100 text-blue-700" 
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  )}
                >
                  <Eye className="w-4 h-4" />
                  <span>Preview</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-white border-b border-gray-200 px-6 py-4">
              <p className="text-gray-500 text-sm">Select a step to start editing</p>
            </div>
          )}
          
          <div className="flex-1 p-6 overflow-auto">
            <AnimatePresence mode="wait">
              {renderStepEditor()}
            </AnimatePresence>
          </div>
        </div>

        {/* Right Sidebar - Collapsible */}
        {selectedStep && (
          <div className={cn(
            "bg-white border-l border-gray-200 flex flex-col transition-all duration-300",
            isRightSidebarCollapsed ? "w-16" : "w-72"
          )}>
            {!isRightSidebarCollapsed ? (
              <div className="p-4 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Step Settings</h3>
                  <button
                    onClick={() => setIsRightSidebarCollapsed(true)}
                    className="p-1 text-gray-500 hover:text-gray-700"
                    title="Collapse"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="space-y-4">

                  {/* PDF URL input for PDF steps */}
                  {selectedStep.type === 'pdf' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        PDF URL
                      </label>
                      <input
                        type="url"
                        value={selectedStep.pdfUrl || ''}
                        onChange={(e) => updateStep(selectedStep.id, { pdfUrl: e.target.value })}
                        placeholder="https://example.com/document.pdf"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the URL of the PDF document to display
                      </p>
                    </div>
                  )}

                  {/* Video URL input for video steps */}
                  {selectedStep.type === 'video' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Video URL
                      </label>
                      <input
                        type="url"
                        value={selectedStep.videoUrl || ''}
                        onChange={(e) => updateStep(selectedStep.id, { videoUrl: e.target.value })}
                        placeholder="https://example.com/video.mp4"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#667eea] focus:border-transparent text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Enter the URL of the video file to embed
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-2 flex flex-col items-center space-y-4 pt-6">
                <button
                  onClick={() => setIsRightSidebarCollapsed(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                  title="Expand settings"
                >
                  <ChevronRight className="w-5 h-5 text-gray-600 rotate-180" />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Media Upload Modal */}
      {showMediaUpload && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowMediaUpload(false)} />
          <motion.div
            className="relative bg-white h-full w-full max-w-4xl mx-auto shadow-xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="h-full overflow-y-auto p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Upload Media</h2>
                <button
                  onClick={() => setShowMediaUpload(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <MediaUploader onUpload={handleMediaUpload} />
            </div>
          </motion.div>
        </div>
      )}

      {/* Media Library Modal */}
      {showMediaLibrary && (
        <MediaLibrary
          onClose={() => setShowMediaLibrary(false)}
          isModal={true}
        />
      )}

      {/* Category Manager Modal */}
      {showCategoryManager && (
        <CategoryManager
          onClose={() => {
            setShowCategoryManager(false);
            loadCategories(); // Reload categories after management
          }}
          onCategorySelect={(category) => {
            setState(prev => ({ ...prev, category: category.name }));
            setShowCategoryManager(false);
            loadCategories(); // Reload to get updated list
          }}
          isModal={true}
        />
      )}

      {/* Glossary Extraction Modal */}
      <GlossaryExtractionModal
        isOpen={showGlossaryExtraction}
        onClose={() => setShowGlossaryExtraction(false)}
        tutorial={{
          title: state.title,
          description: state.description,
          steps: state.steps.map(step => ({
            title: step.title,
            content: step.content || '',
            subsections: step.subsections
          }))
        }}
      />

      {/* PDF Import Modal */}
      <PDFImportModal
        isOpen={showPDFImport}
        onClose={() => setShowPDFImport(false)}
        onImport={handlePDFImport}
      />
    </div>
  );
};

export default TutorialBuilder;
